/**
 * WHERE a juhuklient came from, as opposed to WHICH TOOL made it.
 *
 * `LeadSource` answers the second question and has answered it for a while.
 * This one answers the first, and the difference is the whole reason the growth
 * log exists: a hundred records marked "vorm" still do not say which page or
 * which campaign earned them, and "kust tuli viimane klient" is the one line in
 * docs/arengusuund.md that is said to actually matter.
 *
 * Three columns, filled at capture time and never reconstructed afterwards -
 * the analytics tables carry referrer and utm too, but they are aggregate
 * counters keyed on a pseudonymous daily hash that is pruned on purpose, so
 * there has never been anything to join a record to.
 *
 * Renders nothing when all three are empty, which is every record created
 * before the columns existed and every one typed in by hand. An empty box that
 * says "unknown" three times is worse than no box.
 */
import { ExternalLink, Megaphone, MousePointerClick } from "lucide-react";
import type { Lead } from "../../../lib/clients";
import { useT } from "../../../i18n/I18nProvider";

type T = ReturnType<typeof useT>;

/** A URL shortened to something readable, keeping the part that identifies it. */
function shortUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const path = u.pathname === "/" ? "" : u.pathname;
    return `${u.host.replace(/^www\./, "")}${path}`.slice(0, 64);
  } catch {
    return raw.slice(0, 64);
  }
}

export function LeadOrigin({ lead, t }: { lead: Lead; t: T }) {
  const url = lead.sourceUrl ?? null;
  const ref = lead.sourceRef ?? null;
  const campaign = lead.sourceCampaign ?? null;
  if (!url && !ref && !campaign) return null;

  return (
    <dl className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
      {url && (
        <div className="flex items-center gap-1.5">
          <MousePointerClick className="size-3.5 shrink-0" />
          <dt className="sr-only">{t("leads.origin.page")}</dt>
          <dd>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-primary hover:underline"
              title={url}
            >
              {shortUrl(url)}
              <ExternalLink className="size-3 shrink-0" />
            </a>
          </dd>
        </div>
      )}
      {/* An empty referrer host is a real answer - somebody typed the address or
          followed a link from a mail client - so it is shown as "direct" rather
          than left out, which would look like a measurement that did not run. */}
      <div className="flex items-center gap-1.5">
        <ExternalLink className="size-3.5 shrink-0" />
        <dt className="sr-only">{t("leads.origin.referrer")}</dt>
        <dd>{ref || t("leads.origin.direct")}</dd>
      </div>
      {campaign && (
        <div className="flex items-center gap-1.5">
          <Megaphone className="size-3.5 shrink-0" />
          <dt className="sr-only">{t("leads.origin.campaign")}</dt>
          <dd className="font-medium text-foreground">{campaign}</dd>
        </div>
      )}
    </dl>
  );
}
